import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { aiStudioAPI, api } from '@/lib/api';
import { ArrowLeft, Upload, Loader2, Download, Wand2, Palette, Clock, ImageIcon, Sparkles, User, FileText, Copy, Video, AlertTriangle, Cpu } from 'lucide-react';
import { toast } from 'sonner';

const AIStudio = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  
  const [uploadedImage, setUploadedImage] = useState(null);
  const [imageBase64, setImageBase64] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [result, setResult] = useState(null);
  const [activeTab, setActiveTab] = useState('text2img');
  const [modelPreference, setModelPreference] = useState('gemini');
  
  // Tool options
  const [textPrompt, setTextPrompt] = useState('');
  const [videoPrompt, setVideoPrompt] = useState('');
  const [videoDuration, setVideoDuration] = useState('5');
  const [videoModel, setVideoModel] = useState('minimax');
  const [styleType, setStyleType] = useState('oil painting masterpiece');
  const [ageDirection, setAgeDirection] = useState('20 years older');
  const [backgroundType, setBackgroundType] = useState('');
  const [avatarStyle, setAvatarStyle] = useState('3D Pixar-style animated');

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        toast.error('Image must be less than 10MB');
        return;
      }
      const reader = new FileReader();
      reader.onload = (event) => {
        const base64 = event.target.result.split(',')[1];
        setImageBase64(base64);
        setUploadedImage(event.target.result);
        setResult(null);
        toast.success('Image uploaded!');
      };
      reader.readAsDataURL(file);
    }
  };

  const processImage = async (tool, options) => {
    // Video with MiniMax or Kling doesn't need image, only LongCat does
    const videoNeedsImage = tool === 'video' && options?.model === 'longcat';
    const needsImage = tool !== 'text2img' && tool !== 'video';
    
    if ((needsImage || videoNeedsImage) && !imageBase64) {
      toast.error('Please upload an image first');
      return;
    }
    
    if (tool === 'text2img' && (!textPrompt || textPrompt.length < 5)) {
      toast.error('Please enter a prompt (at least 5 characters)');
      return;
    }
    
    setIsProcessing(true);
    setResult(null);
    
    try {
      const response = await aiStudioAPI.process(
        tool === 'text2img' ? '' : imageBase64, 
        tool, 
        options,
        modelPreference
      );
      
      // Handle Grok video direct response (no polling needed)
      if (response.success && response.tool === 'video' && response.video_url && response.model === 'grok') {
        setResult({
          success: true,
          tool: 'video',
          video_url: response.video_url,
          model: 'grok'
        });
        setIsProcessing(false);
        const creditsMsg = response.credits_used > 0 ? ` (${response.credits_used} credits used)` : '';
        toast.success(`Grok video generated!${creditsMsg}`);
        return;
      }
      
      // Handle fal.ai video polling
      if (response.success && response.status === 'processing' && response.job_id) {
        const model = response.model || 'minimax';
        const waitTime = model === 'longcat' ? '5-10 minutes' : '1-2 minutes';
        toast.info(`Video generation started with ${model}. This takes ${waitTime}...`);
        
        // Poll for video completion
        const pollInterval = setInterval(async () => {
          try {
            const statusRes = await api.get(`/video/status/${response.job_id}`);
            console.log('Poll status:', statusRes.data);
            if (statusRes.data.status === 'completed') {
              clearInterval(pollInterval);
              setResult({
                success: true,
                tool: 'video',
                video_url: statusRes.data.video_url
              });
              setIsProcessing(false);
              toast.success('Video generated successfully!');
            } else if (statusRes.data.status === 'failed') {
              clearInterval(pollInterval);
              setIsProcessing(false);
              toast.error('Video generation failed: ' + (statusRes.data.error || 'Unknown error'));
            }
            // If still processing, continue polling
          } catch (pollError) {
            console.error('Poll error:', pollError);
            // Don't stop polling on network errors
          }
        }, 10000); // Check every 10 seconds
        
        // Timeout after 10 minutes for LongCat, 5 minutes for others
        const timeout = model === 'longcat' ? 600000 : 300000;
        setTimeout(() => {
          clearInterval(pollInterval);
          setIsProcessing(false);
          toast.warning('Video is still generating. Check back later or try a shorter video.');
        }, timeout);
        
        return; // Don't set isProcessing to false yet
      }
      
      if (response.success) {
        setResult(response);
        const modelUsed = response.model_used || modelPreference;
        toast.success(`Processing complete! (${modelUsed})`);
      }
    } catch (error) {
      console.error('Processing error:', error);
      toast.error('Processing failed. Please try again.');
    } finally {
      if (!result?.status) { // Don't turn off if video is still processing
        setIsProcessing(false);
      }
    }
  };

  const handleDownload = () => {
    if (!result || !result.image_data) return;
    try {
      const byteCharacters = atob(result.image_data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: result.mime_type });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `terrylb-${activeTab}-${Date.now()}.png`;
      link.style.display = 'none';
      document.body.appendChild(link);
      setTimeout(() => {
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }, 100);
      toast.success('Image downloading...');
    } catch (error) {
      toast.error('Download failed. Try right-clicking the image.');
    }
  };

  const handleCopyPrompt = () => {
    if (result?.generated_prompt) {
      navigator.clipboard.writeText(result.generated_prompt);
      toast.success('Prompt copied to clipboard!');
    }
  };

  const tools = [
    { id: 'text2img', label: 'Text to Image', icon: Wand2, color: 'from-pink-500 to-rose-500' },
    { id: 'video', label: 'AI Video', icon: Video, color: 'from-red-500 to-orange-500' },
    { id: 'style', label: 'Style Transfer', icon: Palette, color: 'from-purple-500 to-pink-500' },
    { id: 'age', label: 'Age Transform', icon: Clock, color: 'from-blue-500 to-cyan-500' },
    { id: 'background', label: 'Background', icon: ImageIcon, color: 'from-green-500 to-emerald-500' },
    { id: 'enhance', label: 'Enhance', icon: Sparkles, color: 'from-yellow-500 to-orange-500' },
    { id: 'avatar', label: 'Avatar', icon: User, color: 'from-indigo-500 to-purple-500' },
    { id: 'prompt', label: 'Image to Prompt', icon: FileText, color: 'from-rose-500 to-red-500' },
  ];

  const styleOptions = [
    'oil painting masterpiece',
    'watercolor painting',
    'anime/manga style',
    'pencil sketch',
    'pop art like Andy Warhol',
    'impressionist like Monet',
    'cyberpunk neon',
    'vintage 1950s photograph',
    'comic book style',
    'Renaissance painting',
    'abstract art',
    'pixel art retro game',
    'studio ghibli animation',
    'photorealistic HDR'
  ];

  const avatarStyles = [
    '3D Pixar-style animated',
    'Disney princess/prince',
    'anime character',
    'chibi cute',
    'realistic video game character',
    'comic book superhero',
    'cartoon caricature',
    'fantasy RPG character',
    'Simpsons style',
    'South Park style'
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-violet-50 via-purple-50 to-fuchsia-50">
      <nav className="px-6 py-4 bg-white/80 backdrop-blur-sm border-b-4 border-black sticky top-0 z-50">
        <div className="max-w-7xl mx-auto flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard')}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <Wand2 className="h-7 w-7 text-purple-600" />
          <h1 className="text-xl font-bold">AI Studio</h1>
        </div>
      </nav>

      <div className="max-w-6xl mx-auto px-6 py-8">
        <h2 className="text-4xl md:text-5xl font-bold mb-2 text-center">
          <span className="bg-gradient-to-r from-violet-600 to-fuchsia-600 bg-clip-text text-transparent">AI Studio</span>
        </h2>
        <p className="text-lg text-center mb-4 text-gray-700">Transform your photos with powerful AI tools</p>
        
        {/* Model Selector */}
        <div className="flex items-center justify-center gap-3 mb-6">
          <div className="flex items-center gap-2 bg-white/80 backdrop-blur-sm px-4 py-2 rounded-full border-2 border-black shadow-md">
            <Cpu className="h-4 w-4 text-purple-600" />
            <span className="text-sm font-medium text-gray-700">AI Model:</span>
            <div className="flex gap-1">
              <button
                data-testid="model-gemini-btn"
                onClick={() => setModelPreference('gemini')}
                className={`px-3 py-1 rounded-full text-sm font-semibold transition-all ${
                  modelPreference === 'gemini'
                    ? 'bg-gradient-to-r from-blue-500 to-cyan-500 text-white shadow-sm'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                Gemini
              </button>
              <button
                data-testid="model-openai-btn"
                onClick={() => setModelPreference('openai')}
                className={`px-3 py-1 rounded-full text-sm font-semibold transition-all ${
                  modelPreference === 'openai'
                    ? 'bg-gradient-to-r from-green-500 to-emerald-500 text-white shadow-sm'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                OpenAI
              </button>
            </div>
          </div>
          <div className="text-xs text-gray-500 max-w-xs hidden sm:block">
            {modelPreference === 'gemini' 
              ? 'Using Gemini with auto-fallback to OpenAI' 
              : 'Using OpenAI directly'}
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Left - Upload */}
          <Card className="p-5 bg-white border-4 border-black card-shadow">
            <h3 className="text-lg font-bold mb-3">Upload Image</h3>
            <div 
              className="border-4 border-dashed border-gray-300 rounded-lg p-4 text-center cursor-pointer hover:border-purple-500 transition-colors"
              onClick={() => fileInputRef.current?.click()}
            >
              {uploadedImage ? (
                <img src={uploadedImage} alt="Uploaded" className="max-h-48 mx-auto rounded-lg" />
              ) : (
                <div className="py-8">
                  <Upload className="h-10 w-10 mx-auto text-gray-400 mb-2" />
                  <p className="text-gray-600 text-sm">Click to upload</p>
                </div>
              )}
            </div>
            <input ref={fileInputRef} type="file" accept="image/*" onChange={handleFileUpload} className="hidden" />
          </Card>

          {/* Middle - Tools */}
          <Card className="p-5 bg-white border-4 border-black card-shadow">
            <h3 className="text-lg font-bold mb-3">Select Tool</h3>
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="grid grid-cols-4 gap-1 mb-4">
                {tools.slice(0, 4).map(tool => (
                  <TabsTrigger key={tool.id} value={tool.id} className="text-xs px-1">
                    <tool.icon className="h-3 w-3 mr-1" />{tool.label.split(' ')[0]}
                  </TabsTrigger>
                ))}
              </TabsList>
              <TabsList className="grid grid-cols-4 gap-1 mb-4">
                {tools.slice(4).map(tool => (
                  <TabsTrigger key={tool.id} value={tool.id} className="text-xs px-2">
                    <tool.icon className="h-3 w-3 mr-1" />{tool.label.split(' ')[0]}
                  </TabsTrigger>
                ))}
              </TabsList>

              <TabsContent value="text2img" className="space-y-3">
                <p className="text-sm text-gray-600">Create any image from your description</p>
                <textarea 
                  value={textPrompt}
                  onChange={(e) => setTextPrompt(e.target.value)}
                  placeholder="Describe the image you want to create...

Example: A majestic lion with a golden mane standing on a cliff at sunset, dramatic lighting, photorealistic"
                  className="w-full border-2 border-black rounded-lg p-3 min-h-[120px] text-sm"
                />
                <Button 
                  onClick={() => processImage('text2img', { prompt: textPrompt })}
                  disabled={isProcessing || !textPrompt}
                  className="w-full bg-gradient-to-r from-pink-500 to-rose-500 text-white font-bold"
                >
                  {isProcessing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Wand2 className="mr-2 h-4 w-4" />}
                  Generate Image
                </Button>
              </TabsContent>

              <TabsContent value="video" className="space-y-3">
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm flex items-start gap-2">
                  <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
                  <div>
                    <p className="font-semibold text-amber-800">Video Generation</p>
                    <ul className="text-amber-700 text-xs mt-1 space-y-1">
                      <li>• <strong>Grok Imagine:</strong> Best quality, native audio (10 credits/10 sec)</li>
                      <li>• <strong>fal.ai models:</strong> MiniMax, Kling, LongCat (included)</li>
                    </ul>
                  </div>
                </div>
                <textarea 
                  value={videoPrompt}
                  onChange={(e) => setVideoPrompt(e.target.value)}
                  placeholder="Describe the video you want to create...

Example: A cat playing with a ball of yarn in a cozy living room, natural lighting"
                  className="w-full border-2 border-black rounded-lg p-3 min-h-[80px] text-sm"
                />
                <div className="grid grid-cols-2 gap-2">
                  <Select value={videoModel} onValueChange={(val) => {
                    setVideoModel(val);
                    // Reset duration to valid value for new model
                    if (val === 'grok') {
                      setVideoDuration('10');
                    } else if (val !== 'longcat' && parseInt(videoDuration) > 10) {
                      setVideoDuration('5');
                    }
                  }}>
                    <SelectTrigger className="border-2 border-black">
                      <SelectValue placeholder="Model" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="grok">⭐ Grok Imagine (best, w/ audio)</SelectItem>
                      <SelectItem value="minimax">MiniMax (~6 sec, fast)</SelectItem>
                      <SelectItem value="kling">Kling (5-10 sec, HD)</SelectItem>
                      <SelectItem value="longcat">LongCat (up to 3 min)*</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={videoDuration} onValueChange={setVideoDuration}>
                    <SelectTrigger className="border-2 border-black">
                      <SelectValue placeholder="Duration" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="5">5 seconds</SelectItem>
                      <SelectItem value="10">10 seconds</SelectItem>
                      {(videoModel === 'longcat' || videoModel === 'grok') && (
                        <>
                          <SelectItem value="30">30 seconds</SelectItem>
                          <SelectItem value="60">1 minute</SelectItem>
                        </>
                      )}
                      {videoModel === 'longcat' && (
                        <>
                          <SelectItem value="120">2 minutes</SelectItem>
                          <SelectItem value="180">3 minutes</SelectItem>
                        </>
                      )}
                    </SelectContent>
                  </Select>
                </div>
                {videoModel === 'grok' && (
                  <p className="text-xs text-purple-600 font-medium">⭐ Grok Imagine: Best quality with native audio • 1 credit per second</p>
                )}
                {videoModel === 'longcat' && !imageBase64 && (
                  <p className="text-xs text-red-600">* LongCat requires an image upload above</p>
                )}
                <Button 
                  onClick={() => processImage('video', { prompt: videoPrompt, duration: videoDuration, model: videoModel })}
                  disabled={isProcessing || !videoPrompt || (videoModel === 'longcat' && !imageBase64)}
                  className="w-full bg-gradient-to-r from-red-500 to-orange-500 text-white font-bold"
                >
                  {isProcessing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Video className="mr-2 h-4 w-4" />}
                  Generate Video
                </Button>
              </TabsContent>

              <TabsContent value="style" className="space-y-3">
                <p className="text-sm text-gray-600">Transform your photo into stunning artwork</p>
                <Select value={styleType} onValueChange={setStyleType}>
                  <SelectTrigger className="border-2 border-black">
                    <SelectValue placeholder="Select style" />
                  </SelectTrigger>
                  <SelectContent>
                    {styleOptions.map(style => (
                      <SelectItem key={style} value={style}>{style}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button 
                  onClick={() => processImage('style_transfer', { style: styleType })}
                  disabled={isProcessing || !imageBase64}
                  className="w-full bg-gradient-to-r from-purple-500 to-pink-500 text-white font-bold"
                >
                  {isProcessing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Palette className="mr-2 h-4 w-4" />}
                  Apply Style
                </Button>
              </TabsContent>

              <TabsContent value="age" className="space-y-3">
                <p className="text-sm text-gray-600">See yourself at a different age</p>
                <Select value={ageDirection} onValueChange={setAgeDirection}>
                  <SelectTrigger className="border-2 border-black">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="as a young child (5 years old)">Child (5 years)</SelectItem>
                    <SelectItem value="as a teenager (15 years old)">Teenager (15 years)</SelectItem>
                    <SelectItem value="10 years younger">10 Years Younger</SelectItem>
                    <SelectItem value="10 years older">10 Years Older</SelectItem>
                    <SelectItem value="20 years older">20 Years Older</SelectItem>
                    <SelectItem value="as an elderly person (80 years old)">Elderly (80 years)</SelectItem>
                  </SelectContent>
                </Select>
                <Button 
                  onClick={() => processImage('age_transform', { direction: ageDirection })}
                  disabled={isProcessing || !imageBase64}
                  className="w-full bg-gradient-to-r from-blue-500 to-cyan-500 text-white font-bold"
                >
                  {isProcessing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Clock className="mr-2 h-4 w-4" />}
                  Transform Age
                </Button>
              </TabsContent>

              <TabsContent value="background" className="space-y-3">
                <p className="text-sm text-gray-600">Replace your background with any scene</p>
                <Input 
                  value={backgroundType}
                  onChange={(e) => setBackgroundType(e.target.value)}
                  placeholder="e.g., Eiffel Tower, beach sunset, outer space..."
                  className="border-2 border-black"
                />
                <Button 
                  onClick={() => processImage('background_change', { background: backgroundType || 'beautiful sunset beach' })}
                  disabled={isProcessing || !imageBase64}
                  className="w-full bg-gradient-to-r from-green-500 to-emerald-500 text-white font-bold"
                >
                  {isProcessing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ImageIcon className="mr-2 h-4 w-4" />}
                  Change Background
                </Button>
              </TabsContent>

              <TabsContent value="enhance" className="space-y-3">
                <p className="text-sm text-gray-600">Enhance photo quality to professional level</p>
                <div className="p-3 bg-yellow-50 rounded-lg text-sm">
                  <p>✨ Improves: lighting, clarity, colors, sharpness</p>
                  <p>📸 Result: Magazine-quality, 8K HDR finish</p>
                </div>
                <Button 
                  onClick={() => processImage('enhance', {})}
                  disabled={isProcessing || !imageBase64}
                  className="w-full bg-gradient-to-r from-yellow-500 to-orange-500 text-white font-bold"
                >
                  {isProcessing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Sparkles className="mr-2 h-4 w-4" />}
                  Enhance Photo
                </Button>
              </TabsContent>

              <TabsContent value="avatar" className="space-y-3">
                <p className="text-sm text-gray-600">Turn your photo into a stylized avatar</p>
                <Select value={avatarStyle} onValueChange={setAvatarStyle}>
                  <SelectTrigger className="border-2 border-black">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {avatarStyles.map(style => (
                      <SelectItem key={style} value={style}>{style}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button 
                  onClick={() => processImage('avatar', { avatar_style: avatarStyle })}
                  disabled={isProcessing || !imageBase64}
                  className="w-full bg-gradient-to-r from-indigo-500 to-purple-500 text-white font-bold"
                >
                  {isProcessing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <User className="mr-2 h-4 w-4" />}
                  Create Avatar
                </Button>
              </TabsContent>

              <TabsContent value="prompt" className="space-y-3">
                <p className="text-sm text-gray-600">Generate a detailed prompt from any image</p>
                <div className="p-3 bg-blue-50 rounded-lg text-sm">
                  <p>📝 Analyzes: subject, style, colors, lighting, composition</p>
                  <p>🎯 Use to: recreate images or inspire new creations</p>
                </div>
                <Button 
                  onClick={() => processImage('image_to_prompt', {})}
                  disabled={isProcessing || !imageBase64}
                  className="w-full bg-gradient-to-r from-rose-500 to-red-500 text-white font-bold"
                >
                  {isProcessing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <FileText className="mr-2 h-4 w-4" />}
                  Generate Prompt
                </Button>
              </TabsContent>
            </Tabs>
          </Card>

          {/* Right - Result */}
          <Card className="p-5 bg-white border-4 border-black card-shadow">
            <h3 className="text-lg font-bold mb-3">Result</h3>
            {isProcessing ? (
              <div className="flex flex-col items-center justify-center h-64">
                <Loader2 className="h-12 w-12 text-purple-600 animate-spin mb-3" />
                <p className="text-gray-600 text-sm">
                  {activeTab === 'video' ? 'Generating video... (2-5 minutes)' : 'Processing... (30-60 seconds)'}
                </p>
              </div>
            ) : result ? (
              <div className="space-y-3">
                {result.video_url ? (
                  <>
                    <video 
                      src={result.video_url}
                      controls
                      autoPlay
                      loop
                      className="w-full rounded-lg border-2 border-gray-200"
                    />
                    <a 
                      href={result.video_url} 
                      download={`terrylb-video-${Date.now()}.mp4`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <Button className="w-full bg-green-500 text-white font-bold">
                        <Download className="mr-2 h-4 w-4" /> Download Video
                      </Button>
                    </a>
                  </>
                ) : result.image_data ? (
                  <>
                    <img 
                      src={`data:${result.mime_type};base64,${result.image_data}`}
                      alt="Result"
                      className="w-full rounded-lg border-2 border-gray-200"
                    />
                    <Button onClick={handleDownload} className="w-full bg-green-500 text-white font-bold">
                      <Download className="mr-2 h-4 w-4" /> Download
                    </Button>
                  </>
                ) : result.generated_prompt ? (
                  <div className="space-y-3">
                    <div className="p-3 bg-gray-50 rounded-lg max-h-64 overflow-y-auto">
                      <p className="text-sm whitespace-pre-wrap">{result.generated_prompt}</p>
                    </div>
                    <Button onClick={handleCopyPrompt} className="w-full bg-blue-500 text-white font-bold">
                      <Copy className="mr-2 h-4 w-4" /> Copy Prompt
                    </Button>
                  </div>
                ) : null}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-64 text-gray-400">
                <Wand2 className="h-12 w-12 mb-3 opacity-50" />
                <p className="text-center text-sm">Upload an image and select a tool to see the magic!</p>
              </div>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
};

export default AIStudio;
